<div class="edit-space-container">
    <jjames-card title="Edit Parking Space">
        <!-- Loading State -->
        @if (isLoading) {
        <div class="loading-container">
            <jjames-loading [center]="true" text="Loading parking space details..."></jjames-loading>
        </div>
        }

        <!-- Form Content -->
        @if (!isLoading) {
        <div class="form-container">
            <!-- Success Alert -->
            @if (successMessage) {
            <jjames-alert type="success" [dismissible]="true" class="alert-spacing">
                {{ successMessage }}
            </jjames-alert>
            }

            <!-- Error Alert -->
            @if (errorMessage) {
            <jjames-alert type="error" [dismissible]="true" class="alert-spacing">
                {{ errorMessage }}
            </jjames-alert>
            }

            <form [formGroup]="editSpaceForm" (ngSubmit)="onSubmit()" class="edit-space-form">

                @if (!isAdmin()) {
                <jjames-alert type="info" class="alert-spacing">
                    Some settings on this page change what happens to a car at the barrier. They are shown
                    but locked — an administrator can change them.
                </jjames-alert>
                }

                <fieldset [disabled]="isSubmitting" class="edit-space-fieldset">

                <!-- Name -->
                <div class="form-field">
                    <jjames-input formControlName="name" label="Name" placeholder="Enter parking space name"
                        [required]="true" [variant]="isFieldInvalid('name') ? 'error' : 'default'">
                    </jjames-input>
                    <p class="field-hint">What parkers and operators see this site called. Safe to change.</p>
                    @if (isFieldInvalid('name')) {
                    <p class="error-message">{{ getFieldError('name') }}</p>
                    }
                </div>

                <!-- Description -->
                <div class="form-field">
                    <jjames-input formControlName="description" label="Description"
                        placeholder="Enter description (optional)"
                        [variant]="isFieldInvalid('description') ? 'error' : 'default'">
                    </jjames-input>
                    <p class="field-hint">Internal note for your team. Not shown to parkers.</p>
                    @if (isFieldInvalid('description')) {
                    <p class="error-message">{{ getFieldError('description') }}</p>
                    }
                </div>

                <!-- Parking Space Type -->
                <div class="form-field">
                    <label class="form-label">Space Type <span class="required">*</span></label>
                    <select formControlName="parkingSpaceType" class="form-select">
                        @for (type of parkingSpaceTypes; track type) {
                        <option [value]="type">{{ type }}</option>
                        }
                    </select>
                    <p class="field-hint">Classification of the site. Used for reporting and filtering.</p>
                </div>

                <!-- Access Mode -->
                <div class="form-field">
                    <label class="form-label">Access Mode <span class="required">*</span></label>
                    <select formControlName="accessMode" class="form-select">
                        @for (mode of accessModes; track mode) {
                        <option [value]="mode">{{ mode }}</option>
                        }
                    </select>
                    <p class="field-hint">
                        <strong>Open</strong> — anyone may drive in and pays on the way out.
                        <strong>Closed</strong> — only vehicles with a parking right get in. Switching to Closed
                        turns away every short-term parker from the next detection onward.
                        @if (!isAdmin()) { <span class="field-hint-lock">Administrators only.</span> }
                    </p>
                </div>

                <!-- Entry Type -->
                <div class="form-field">
                    <label class="form-label">Entry Type <span class="required">*</span></label>
                    <select formControlName="entryType" class="form-select">
                        @for (type of entryTypes; track type) {
                        <option [value]="type">{{ type }}</option>
                        }
                    </select>
                    <p class="field-hint">
                        <strong>Gated</strong> — barriers hold a car until it is allowed out.
                        <strong>Free flow</strong> — no barrier, so an unpaid stay ends at the exit and the debt
                        is collected afterwards. Only set Free flow where there really is no barrier.
                        @if (!isAdmin()) { <span class="field-hint-lock">Administrators only.</span> }
                    </p>
                </div>

                <!-- Exit & payment behaviour -->
                <div class="section-divider">
                    <span class="section-title">Exit &amp; Payment</span>
                </div>

                <!-- QR / Stripe pay channel at the exit -->
                <div class="form-field">
                    <div class="toggle-row">
                        <div class="toggle-copy">
                            <div class="toggle-title">QR payment at the exit</div>
                            <div class="toggle-hint">
                                Show a QR code on the exit display that the parker scans to pay for their stay
                                on their phone. Needs a display at the exit and a payment page configured —
                                without a display there is nothing to show the code on.
                                @if (!isAdmin()) { <span class="field-hint-lock">Administrators only.</span> }
                            </div>
                        </div>
                        <label class="switch">
                            <input type="checkbox" formControlName="qrPaymentEnabled" />
                            <div class="switch-track"></div>
                        </label>
                    </div>
                </div>

                <!-- Pay-at-exit presence window (R-20) -->
                <div class="form-field">
                    <jjames-input formControlName="exitPaymentPresenceWindowMinutes"
                        label="Paid-exit presence window (minutes)" placeholder="Leave empty for the default (15)"
                        type="number"
                        [variant]="isFieldInvalid('exitPaymentPresenceWindowMinutes') ? 'error' : 'default'">
                    </jjames-input>
                    <p class="text-sm text-gray-500 mt-2">
                        After a payment lands, the system looks back this many minutes for the
                        "pay at the barrier" screen as proof the car is still there, before opening
                        the gate. This is a match tolerance, not a wait. Enter <strong>0</strong> to
                        go back to the global default.
                        @if (!isAdmin()) { <span class="field-hint-lock">Administrators only.</span> }
                    </p>
                </div>

                <!-- Fake drive-in -->
                <div class="form-field">
                    <div class="toggle-row">
                        <div class="toggle-copy">
                            <div class="toggle-title">Allow exit without a recorded entry</div>
                            <div class="toggle-hint">
                                Let a vehicle out when no entry was recorded for it — a missed camera read
                                should not trap someone at the barrier. Turning it off means any car the entry
                                camera missed is stuck at the exit until someone intervenes.
                                @if (!isAdmin()) { <span class="field-hint-lock">Administrators only.</span> }
                            </div>
                        </div>
                        <label class="switch">
                            <input type="checkbox" formControlName="allowFakeDriveIn" />
                            <div class="switch-track"></div>
                        </label>
                    </div>
                </div>

                <!-- Hotline -->
                <div class="form-field">
                    <jjames-input formControlName="hotline" label="Hotline" placeholder="e.g. +43 1 234 5678"
                        [variant]="isFieldInvalid('hotline') ? 'error' : 'default'">
                    </jjames-input>
                    <p class="text-sm text-gray-500 mt-2">
                        Phone number shown to parkers on this site's payment stations. Leave empty to advertise
                        none.
                    </p>
                    @if (isFieldInvalid('hotline')) {
                    <p class="error-message">{{ getFieldError('hotline') }}</p>
                    }
                </div>

                <!-- Late payment lives on its own editor — see the note below -->
                <div class="form-field">
                    <p class="text-sm text-gray-500">
                        <strong>Late payment</strong> (free-flow debt collection) is edited from the space's
                        details page, not here. Its settings are saved as one block so that clearing the
                        amount ceiling means "uncapped" — this form treats an empty field as "leave
                        unchanged", which cannot express that.
                    </p>
                </div>

                <div class="section-divider">
                    <span class="section-title">Other</span>
                </div>

                <!-- Old Parking Site ID -->
                <div class="form-field">
                    <jjames-input formControlName="oldParkingSiteId" label="Old Parking Site ID"
                        placeholder="Legacy site ID (optional)" type="number"
                        [variant]="isFieldInvalid('oldParkingSiteId') ? 'error' : 'default'">
                    </jjames-input>
                    <p class="field-hint">
                        Links this site to its record on the old platform. Imports match on it, so changing it
                        re-points this site at a different legacy site's data.
                        @if (!isAdmin()) { <span class="field-hint-lock">Administrators only.</span> }
                    </p>
                </div>

                <!-- EFSTA register (TL/TT) -->
                <div class="form-field">
                    <jjames-input formControlName="fiscalTransactionLocation" label="EFSTA location (TL)"
                        [variant]="isFieldInvalid('fiscalTransactionLocation') ? 'error' : 'default'">
                    </jjames-input>
                    <jjames-input formControlName="fiscalTransactionTerminal" label="EFSTA terminal (TT)" class="mt-2"
                        [variant]="isFieldInvalid('fiscalTransactionTerminal') ? 'error' : 'default'">
                    </jjames-input>
                    @if (editSpaceForm.hasError('fiscalRegisterPair')) {
                    <p class="field-hint text-red-600">Set both or neither.</p>
                    }
                    @if (!isAdmin()) { <p class="field-hint"><span class="field-hint-lock">Administrators only.</span></p> }
                </div>

                <!-- Self-service license-plate cap (webshop) -->
                <div class="form-field">
                    <jjames-input formControlName="maxSelfServiceLicensePlates"
                        label="Max. self-registered license plates" placeholder="Leave empty for no limit" type="number"
                        [variant]="isFieldInvalid('maxSelfServiceLicensePlates') ? 'error' : 'default'">
                    </jjames-input>
                    <p class="text-sm text-gray-500 mt-2">
                        How many license plates a <strong>student or employee</strong> may register themselves in
                        the webshop for this space. Empty = no limit. Suppliers and all other classifications
                        are never capped — their plates are credentialed by an operator via the hotline.
                    </p>
                </div>

                <!-- Cars inside at once per Dauerparkrecht (parker pool cap) -->
                <div class="form-field">
                    <jjames-input formControlName="maxConcurrentCarsPerParkerPool"
                        label="Max. cars inside per long-term right" placeholder="Leave empty to keep" type="number"
                        [variant]="isFieldInvalid('maxConcurrentCarsPerParkerPool') ? 'error' : 'default'">
                    </jjames-input>
                </div>

                <!-- Self-service license-plate change cooldown (webshop) -->
                <div class="form-field">
                    <jjames-input formControlName="licensePlateChangeCooldownDays"
                        label="License plate change cooldown (days)"
                        placeholder="Leave empty for no limit" type="number"
                        [variant]="isFieldInvalid('licensePlateChangeCooldownDays') ? 'error' : 'default'">
                    </jjames-input>
                    <p class="text-sm text-gray-500 mt-2">
                        Minimum days a parker must wait before registering another new plate in the webshop at
                        this space. Empty = no limit. Applies to every parker, not just students/employees.
                        Only throttles adding a plate — removing one is never blocked.
                    </p>
                </div>

                <!-- Governing organisation (SSO membership) -->
                <div class="form-field">
                    <label class="form-label">Governing organisation</label>
                    <select formControlName="externalOrganizationId" class="form-select">
                        <option [ngValue]="null">None — operator-governed</option>
                        @for (org of organizations(); track org.id) {
                        <option [ngValue]="org.id">{{ org.name }}</option>
                        }
                    </select>
                    <p class="text-sm text-gray-500 mt-2">
                        When set, anyone signing in through this organisation's SSO at this space is added
                        as a <strong>member of that organisation</strong>. That is a prerequisite, not a
                        permission: it grants no parking on its own, it is what lets you afterwards place
                        the person into one of the organisation's pools.
                        @if (!isAdmin()) { <span class="field-hint-lock">Administrators only.</span> }
                    </p>
                </div>

                <!-- Restricted-access (visitor) window — Closed spaces only -->
                @if (isClosedSpace) {
                <div class="section-divider">
                    <span class="section-title">Visitor Access Window</span>
                </div>
                <div class="form-field">
                    <p class="text-sm text-gray-500 mb-3">
                        This space is Closed (authorized vehicles only). Optionally open it to visitors during a daily
                        window — they pay short-term while it is open and are denied outside it.
                    </p>
                    <app-access-window-field [(value)]="restrictedWindow"
                        enabledLabel="Open this closed space to visitors during a daily window"
                        description="Visitors may only enter during these hours (camera-local). Registered, pool and exempt vehicles are unaffected."
                        defaultStart="17:00" defaultEnd="23:59" />
                </div>
                }

                <!-- Address Section -->
                <div class="section-divider">
                    <span class="section-title">Address</span>
                </div>

                <div class="form-field">
                    <jjames-input formControlName="firstAddressLine" label="Address Line 1"
                        placeholder="Enter first address line (optional)"
                        [variant]="isFieldInvalid('firstAddressLine') ? 'error' : 'default'">
                    </jjames-input>
                </div>

                <div class="form-field">
                    <jjames-input formControlName="secondAddressLine" label="Address Line 2"
                        placeholder="Enter second address line (optional)"
                        [variant]="isFieldInvalid('secondAddressLine') ? 'error' : 'default'">
                    </jjames-input>
                </div>

                <div class="form-row">
                    <div class="form-field">
                        <jjames-input formControlName="locality" label="City / Locality" placeholder="City"
                            [variant]="isFieldInvalid('locality') ? 'error' : 'default'">
                        </jjames-input>
                    </div>
                    <div class="form-field">
                        <jjames-input formControlName="postCode" label="Post Code" placeholder="Post code"
                            [variant]="isFieldInvalid('postCode') ? 'error' : 'default'">
                        </jjames-input>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-field">
                        <jjames-input formControlName="region" label="Region" placeholder="Region"
                            [variant]="isFieldInvalid('region') ? 'error' : 'default'">
                        </jjames-input>
                    </div>
                    <div class="form-field">
                        <jjames-input formControlName="countryCode" label="Country Code" placeholder="e.g. GB"
                            [variant]="isFieldInvalid('countryCode') ? 'error' : 'default'">
                        </jjames-input>
                    </div>
                </div>

                </fieldset>

                <!-- Action Buttons -->
                <div class="form-actions">
                    <jjames-button type="button" variant="secondary" (click)="onCancel()" [disabled]="isSubmitting">
                        Cancel
                    </jjames-button>
                    <jjames-button type="submit" variant="primary" [loading]="isSubmitting"
                        [disabled]="!editSpaceForm.valid || isSubmitting">
                        Update Parking Space
                    </jjames-button>
                </div>
                @if (isSubmitting && editSpaceForm.get('maxConcurrentCarsPerParkerPool')?.dirty) {
                <p class="text-sm text-gray-500 mt-2">
                    Changing the cars-inside cap re-syncs every parker pool at this space —
                    this can take a little longer than a normal save.
                </p>
                }
            </form>
        </div>
        }
    </jjames-card>
</div>